import React, { useMemo } from 'react';
import { Text, Heading, Box, Select, MenuItemProps } from '@televet/kibble-ui';
import { formattedSendingHours } from 'pages/Automations';
import { WorkflowSendingHours as AutomationSendingHours } from 'shared/types/graphql';
import { useSurveySettingsContext } from 'pages/Survey/context/SurveySettingsContext';
import { SurveySettingsActions } from 'pages/Survey/context/SurveySettingsReducer';
import { RouteDefinitions, SettingsRoutes } from 'routes';
import { Link } from 'react-router-dom';

const SendHours = (): JSX.Element => {
  const {
    composeSettings,
    composeSettings: { sendHours },
    dispatch,
  } = useSurveySettingsContext();

  /*
   * Even though send hours are visible on the 'settings' tab, they are configured at the
   * automation (rating workflow) level and cannot be changed using the `updateSurvyGeneralSettings`
   * mutation. Therefore, this field is stored under 'composeSettings' so it can be updated
   * using the 'updateWorkflowEventSetting' like the other compose tab settings
   */
  const handleSendHoursChange = ({ value }: MenuItemProps): void => {
    if (!value) return;
    dispatch({
      type: SurveySettingsActions.UPDATE_COMPOSE_SETTINGS,
      payload: { ...composeSettings, sendHours: value },
    });
    dispatch({ type: SurveySettingsActions.SET_RATING_WORKFLOW_CHANGED, payload: true });
  };

  const sendHoursOptions = useMemo(
    () => [
      {
        label: formattedSendingHours[AutomationSendingHours.All24Hours],
        value: AutomationSendingHours.All24Hours,
        isSelected: sendHours === AutomationSendingHours.All24Hours,
      },
      {
        label: formattedSendingHours[AutomationSendingHours.AutomationHours],
        value: AutomationSendingHours.AutomationHours,
        isSelected: sendHours === AutomationSendingHours.AutomationHours,
      },
      {
        label: formattedSendingHours[AutomationSendingHours.BusinessHours],
        value: AutomationSendingHours.BusinessHours,
        isSelected: sendHours === AutomationSendingHours.BusinessHours,
      },
    ],
    [sendHours],
  );

  return (
    <Box className="settings__container" mt="24px">
      <Heading fontWeight="bold" className="settings__header" size="md" mb={2}>
        Send Hours
      </Heading>
      <Text className="settings__description" variant="subtle" mb={4}>
        Send hours determine what day and time this survey is able to send. Automation Hours can be adjusted in{' '}
        <Link to={`${RouteDefinitions.Settings}${SettingsRoutes.ClinicAutomations}`}>
          <Text variant="interactive" textDecoration="underline">
            Settings
          </Text>
        </Link>
        .
      </Text>
      <Select
        mt={4}
        value={sendHours || AutomationSendingHours.BusinessHours}
        options={sendHoursOptions}
        listProps={{ onSelect: handleSendHoursChange, maxW: '100%' }}
      />
    </Box>
  );
};

export default SendHours;
